import React from 'react'
import { motion, MotionProps } from 'framer-motion'
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver'
import { usePrefersReducedMotion } from '../../hooks/useResponsive'

interface FadeInUpProps extends Omit<MotionProps, 'initial' | 'animate' | 'variants'> {
  children: React.ReactNode
  delay?: number
  duration?: number
  distance?: number
  triggerOnce?: boolean
  className?: string
}

export function FadeInUp({
  children,
  delay = 0,
  duration = 0.6,
  distance = 30,
  triggerOnce = true,
  className,
  ...motionProps
}: FadeInUpProps) {
  const [ref, isIntersecting] = useIntersectionObserver<HTMLDivElement>({
    triggerOnce,
    threshold: 0.1,
  })
  const prefersReducedMotion = usePrefersReducedMotion()

  const variants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : distance,
    },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  const transition = {
    duration: prefersReducedMotion ? 0.01 : duration,
    delay: prefersReducedMotion ? 0 : delay,
  }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isIntersecting ? 'visible' : 'hidden'}
      variants={variants}
      transition={transition}
      className={className}
      {...motionProps}
    >
      {children}
    </motion.div>
  )
}

interface StaggeredFadeInUpProps {
  children: React.ReactNode[]
  staggerDelay?: number
  itemDelay?: number
  duration?: number
  distance?: number
  className?: string
  itemClassName?: string
}

export function StaggeredFadeInUp({
  children,
  staggerDelay = 0.1,
  itemDelay = 0,
  duration = 0.6,
  distance = 30,
  className,
  itemClassName,
}: StaggeredFadeInUpProps) {
  const [ref, isIntersecting] = useIntersectionObserver<HTMLDivElement>({
    triggerOnce: true,
    threshold: 0.1,
  })
  const prefersReducedMotion = usePrefersReducedMotion()

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : staggerDelay,
        delayChildren: prefersReducedMotion ? 0 : itemDelay,
      },
    },
  }

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : distance,
    },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  const itemTransition = {
    duration: prefersReducedMotion ? 0.01 : duration,
  }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isIntersecting ? 'visible' : 'hidden'}
      variants={containerVariants}
      className={className}
    >
      {children.map((child, index) => (
        <motion.div
          key={index}
          variants={itemVariants}
          transition={itemTransition}
          className={itemClassName}
        >
          {child}
        </motion.div>
      ))}
    </motion.div>
  )
}